/**
 * Taking a lead off the board and onto Deals.
 *
 * It was `window.confirm("Move X to Deals?")` — a browser alert with two
 * buttons, no sign of what was being moved beyond a name, and nothing
 * recorded about why. The row then appeared on Deals with no answer to the
 * only question anybody asks of it a week later: what made this one worth
 * chasing?
 *
 * So it asks. The note is the point of the screen, not a field on it: it is
 * what shows on the Deals row underneath the name, and it is written into
 * the trail as the line that closes the lead's time on the board.
 *
 * Everything else here is read, not typed. The capture, the source and the
 * owner are already known and cannot be improved by being asked again; they
 * are shown so the person pressing the button can see they have the right
 * row. The one thing only they know is why.
 */
import { useState } from 'react';
import { useApp } from '../../store/AppContext.jsx';
import Icon from '../../lib/icons.jsx';
import { formatDate } from '../../lib/dates.js';
import Sheet, { Group, Field } from '../Sheet.jsx';
import { Avatar, EmptyState } from '../Primitives.jsx';
import { companyName, leadDetails } from '../../domain/crm.js';

/* What people actually write, offered as one press so the note is not blank
   on the rows where it would otherwise be typed the same way every time. */
const REASONS = [
  'Demo booked',
  'Pricing sent',
  'Asked for a proposal',
  'Ready to buy',
  'Signed up'
];

export default function MoveToDealSheet({ id, onClose }) {
  const { S, crm, me, moveDealStage, saveActivity, toast } = useApp();
  const d = crm.deals.find(x => x.id === id);
  const [why, setWhy] = useState('');

  if (!d) {
    return (
      <Sheet icon="check" title="Move to deal" onClose={onClose} closeLabel="Close">
        <div style={{ gridColumn: '1/-1' }}>
          <EmptyState icon="alert" tone="g-red" title="That row is no longer on the board" />
        </div>
      </Sheet>
    );
  }

  const f = leadDetails(crm, d);

  const move = () => {
    const note = why.trim();
    /*
     * The note first, then the move.
     *
     * moveDealStage writes its own "Moved to Won" line and the row leaves
     * this board; writing the reason afterwards would put it under a stage
     * change it is the explanation for. Both are stamped today, so the order
     * they go in is the order they read in.
     */
    if (note) {
      saveActivity(null, {
        subject: 'Moved to Deals',
        type: 'Note',
        /* Whoever pressed it, not the workspace name: this is a decision
           somebody made and the Deals row says who. */
        who: (me && me.name) || S.senderName || '',
        dealId: d.id,
        due: formatDate(new Date(), false, S.dateFmt),
        done: true,
        notes: note
      });
    }
    moveDealStage(d.id, 'Won');
    onClose();
    toast(`${d.title} is on Deals`);
  };

  return (
    <Sheet
      icon="check"
      tone="g-green"
      title="Move to deal"
      onClose={onClose}
      onSave={move}
      saveLabel="Move to deal"
      closeLabel="Not yet"
    >
      <Group>What is moving</Group>

      <Field label="" wide>
        {/*
          * Every part of this named, rather than picked out by position.
          * `div:first-of-type` matched Avatar — which is a div — so the
          * avatar took the whole row and the name and address ran together
          * beside it with nothing between them.
          */}
        <div className="mtd-who">
          <Avatar name={d.title} className="mtd-ava" />

          <div className="mtd-id">
            <b>{d.title}</b>
            {f.email ? <span>{f.email}</span> : null}
            {/* Only when it is something other than the address again: the
                tracker files a form under whatever it has, and half these
                rows carry the same string twice. */}
            {f.phone ? <span>{f.phone}</span> : null}
            {companyName(crm, d.cid) && companyName(crm, d.cid) !== d.title
              ? <span>{companyName(crm, d.cid)}</span> : null}
            {!f.email && !f.phone ? <span className="mtd-none">no contact details</span> : null}
          </div>

          <div className="mtd-tags">
            {f.type ? <span className="mtd-tag">{f.type}</span> : null}
            {f.stateLabel ? (
              <span className={`mtd-tag ${f.state === 'submitted' ? 'sub' : 'part'}`}>
                {f.stateLabel}
              </span>
            ) : null}
            {d.source ? <span className="mtd-tag src">{d.source}</span> : null}
          </div>
        </div>
      </Field>

      {/* One heading, not two. It was "Why" over "What made this one a deal",
          which is the same question asked twice with the answer box under the
          second one. */}
      <Group>Why it is a deal</Group>

      <Field label="" wide>
        <div className="mtd-quick">
          {REASONS.map(r => (
            <button
              key={r}
              type="button"
              className={`mtd-chip${why.trim() === r ? ' on' : ''}`}
              onClick={() => setWhy(why.trim() === r ? '' : r)}
            >
              {why.trim() === r ? <Icon name="check" /> : null}{r}
            </button>
          ))}
        </div>
        <textarea
          autoFocus
          style={{ minHeight: 92 }}
          value={why}
          placeholder="They booked the demo for Thursday and asked for the annual price."
          onChange={e => setWhy(e.target.value)}
        />
        <small className="mtd-hint">
          This shows on the Deals row under the name, signed with yours.
        </small>

        {/* Said plainly rather than left to be discovered. Leaving the board
            is the one thing here that pressing the button again will not
            undo. */}
        <p className="mtd-warn">
          <Icon name="alert" />
          It leaves the pipeline and appears on Deals. Everything the tracker
          saw goes with it.
        </p>
      </Field>
    </Sheet>
  );
}
